import { processLock } from '@supabase/auth-js';
import { supabaseAuthStorage } from './native-storage';

// Auth options for the Supabase client, extracted here so the lock choice below
// is unit-testable without importing the env-dependent `supabase` singleton
// (which calls createClient at module load).
//
// WHY `lock: processLock` on EVERY platform (web + native):
// @supabase/auth-js 2.94 wraps every auth op (getSession, signInWithPassword,
// token refresh) in a lock. When `isBrowser() && navigator.locks` it defaults to
// `navigatorLock`, backed by the Web Locks API and shared per-origin across tabs.
// That default breaks sign-in on BOTH platforms, for different reasons:
//
//   - Native (Capacitor WKWebView): navigator.locks exists but is the buggy impl
//     auth-js's own locks.js flags — iOS throws a bare `TypeError` ("Type error")
//     the instant sign-in tries to acquire, before any network request (#936).
//
//   - Web (real browsers, Chrome included): the login path fires a burst of
//     concurrent auth ops under the single per-origin lock — Auth.tsx runs
//     signOut + signInWithPassword + profile queries + an RPC while AuthContext's
//     lifecycle independently runs getSession + fetchProfile. navigatorLock aborts
//     the contended acquisitions with "AbortError: signal is aborted without
//     reason", so a successful sign-in is followed by every follow-up read
//     aborting: the app hangs on "loading" and bounces /auth ↔ /dashboard.
//     Confirmed in prod error_logs on Chrome 151 / macOS, recurring across weeks.
//     Widening `lockAcquireTimeout` (below) did NOT fix it — those aborts fire in
//     ~50ms clusters, not after the timeout, so they are not timeout aborts.
//
// `processLock` is auth-js's in-memory promise-chain lock: it still serializes
// concurrent auth ops within a tab (correct for this app) but never touches
// `navigator.locks`, so neither failure mode can occur. The one thing it gives up
// is cross-tab coordination of token refresh — rare for this app's users and
// self-healing on the next load, a good trade against sign-in breaking outright.
//
// `lockAcquireTimeout` is kept as belt-and-suspenders: processLock ignores it, but
// if the lock choice is ever reverted the 25s ceiling (up from the auth-js 2.94
// default of 10s) keeps a contended navigatorLock acquire a wait, not a hard abort.
export const SUPABASE_AUTH_OPTIONS = {
  storage: supabaseAuthStorage,
  persistSession: true,
  autoRefreshToken: true,
  detectSessionInUrl: false,
  lockAcquireTimeout: 25_000,
  lock: processLock,
} as const;
